import React, { useState, useEffect } from 'react';
import { Redirect, useHistory, useParams, Route } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { useStore } from '../../../core/context/store';
import { useAuthState } from '../../../core/context/auth';
import { useEffectOnce, useHideHubspotWidgetOnMount } from '../../../core/hooks';
import { decodeToken } from '../../../core/lib/jwt';
import { RoutePageProps } from '../../../core/routing';
import { LocationState } from '../../../core/types/location';
import { standardSequencesActions } from '../actions';
import { useIsInstanceRunning } from '../hooks';
import { InactivityNoticeModal } from '../components/simulator/inactivity-notice-modal';
import { InstanceNotRunningModal } from '../components/simulator/instance-not-running-modal';

import '../../../assets/styles/simulator.scss';
import { ISequenceItem, SessionProps } from '../models';
import { ExperimentProgress, SimulatorStatus } from '../components/simulator/models';
import { REAL_MRI_ANATOMICAL_MODEL, renderAreas } from '../constants/simulator-constants';
import { TIME_IN_MS_UNTIL_INACTIVITY_MODAL_IS_SHOWN } from '../constants';
import { useKeepSessionAlive } from '../api';

interface RouteParams {
  sessionId?: string;
}

const SimulationWrapper: React.FC<RoutePageProps> = ({ route }: RoutePageProps): React.ReactElement => {
  const history = useHistory<LocationState>();
  const [, runningItemStoreActions] = useStore<ISequenceItem>('running-experiment', {} as ISequenceItem);
  const [activeSessionStore, activeSessionStoreActions] = useStore<SessionProps>('active-session');
  const [simulatorStatusStore] = useStore<SimulatorStatus>('simulator-status');
  const [cineImagesLoadingLeftStore] = useStore<boolean>(`cine-images-loading-${renderAreas.leftViewer}`);
  const [cineImagesLoadingCenterStore] = useStore<boolean>(`cine-images-loading-${renderAreas.centerViewer}`);
  const [cineImagesLoadingRightStore] = useStore<boolean>(`cine-images-loading-${renderAreas.rightViewer}`);
  const [experimentProgressStore] = useStore<ExperimentProgress>('running-experiment-progress');
  const { data: simStatus } = simulatorStatusStore;
  const { data: sessionData } = activeSessionStore;
  const { data: experimentStatus } = experimentProgressStore;
  const isLiteSim =
    sessionData?.lab?.anatomicalModelId && sessionData?.lab?.anatomicalModelId === REAL_MRI_ANATOMICAL_MODEL;

  useHideHubspotWidgetOnMount();

  const params = useParams<RouteParams>();
  const sessionId = params.sessionId ? parseInt(params.sessionId, 10) : -1;
  const { accessToken } = useAuthState();
  const cineLoading =
    cineImagesLoadingRightStore.data || cineImagesLoadingCenterStore.data || cineImagesLoadingLeftStore.data;

  const { isInstanceRunning } = useIsInstanceRunning();

  const [showInactivityNoticeModal, setShowInactivityNoticeModal] = useState(false);
  const {
    start: startIdleTimer,
    pause: pauseIdleTimer,
    reset: resetIdleTimer,
  } = useIdleTimer({
    name: 'timeout-until-idle-modal-pops-up',
    timeout: TIME_IN_MS_UNTIL_INACTIVITY_MODAL_IS_SHOWN,
    debounce: 350,
    element: document,
    crossTab: true,
    startManually: true,
    startOnMount: false,
    onIdle: () => setShowInactivityNoticeModal(true),
  });
  const { refetch: keepSessionAlive } = useKeepSessionAlive({ sessionId });

  useEffectOnce(() => {
    return () => {
      runningItemStoreActions.reset();
      if (sessionData?.session?.closedAt) {
        // redirect if session closed
        history.push('/courses');
      } else {
        activeSessionStoreActions.dispatch(standardSequencesActions.clearSessionData());
      }
    };
  });

  const simStatusType = simStatus?.type || simStatus?.status;
  useEffect(() => {
    if (simStatusType === 'ready' && !cineLoading && experimentStatus?.type !== 'kernel' && !isLiteSim) {
      startIdleTimer();
    } else {
      pauseIdleTimer();
    }
  }, [pauseIdleTimer, simStatusType, startIdleTimer, cineLoading, experimentStatus, isLiteSim]);

  if (route === null || route === undefined || !accessToken) {
    return <Redirect to="/" />;
  }

  const userToken = decodeToken(accessToken);
  const pagePermissions = route.permissions || [];

  if (pagePermissions.length > 0 && pagePermissions.filter((p) => userToken.scope.includes(p)).length === 0) {
    return <Redirect to="/forbidden" />;
  }

  if (simStatus?.message?.includes('Unable to Get Experiment: Could not connect to Redis')) {
    history.replace('/courses', {
      labId: sessionData?.lab.id,
      labName: sessionData?.lab.name,
      navigationReason: 'redis-no-connection',
    });
  }

  if (sessionData?.session?.closedAt) {
    history.push('/courses'); // TODO: Should we pass a message here as well?
  }

  const onStayButtonClicked = () => {
    // If user stays, the inactivity notice will be closed and the idle timer will reset
    setShowInactivityNoticeModal(false);
    resetIdleTimer();
    keepSessionAlive();
  };

  return (
    <>
      <InstanceNotRunningModal sessionId={sessionId} showModal={!isInstanceRunning} />
      {showInactivityNoticeModal && (
        <InactivityNoticeModal
          onClose={onStayButtonClicked}
          sessionId={sessionId}
          labId={sessionData?.lab.id}
          labName={sessionData?.lab.name}
        />
      )}
      {route?.routes?.map((r) => (
        <Route key={r.path} path={r.path} exact={r.exact} sensitive={r.sensitive} strict={route.strict}>
          {r.layout ? <r.layout route={r} /> : <r.page route={r} />}
        </Route>
      ))}
    </>
  );
};

export default SimulationWrapper;
